import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SrIcon } from "@/components/ui/SrIcon";
import { useSystemConfigString, useUpdateSystemConfigString } from "@/hooks/use-system-config";

const KEY = "crm_style_guide";

/**
 * Drift → CRM: stilguiden som ligger til grunn for alle mailutkast (CRM-2.8).
 * Tom verdi betyr at utkastene faller tilbake til standardteksten i edge function.
 */
export function CrmStyleGuidePanel() {
  const { data: stored = "", isLoading } = useSystemConfigString(KEY);
  const update = useUpdateSystemConfigString();
  const [value, setValue] = useState(stored);

  useEffect(() => setValue(stored), [stored]);

  const dirty = value.trim() !== stored.trim();

  const save = async () => {
    try {
      await update.mutateAsync({ key: KEY, value });
      toast.success("Stilguiden er lagret");
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SrIcon name="pen" className="h-4 w-4 text-muted-foreground" />
          Stilguide for mailutkast
        </CardTitle>
        <CardDescription>
          Claude skriver utkast i denne tonen. De faste reglene (norsk bokmål, ingen tankestreker, maks 120 ord i
          førsteutkast) legges alltid til. Tom stilguide gir standardteksten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <>
            <Label htmlFor="crm-style-guide" className="text-xs">
              Stilguide
            </Label>
            <Textarea
              id="crm-style-guide"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={12}
              className="text-sm"
            />
            <div className="flex justify-end gap-2">
              {dirty && (
                <Button variant="outline" size="sm" onClick={() => setValue(stored)} disabled={update.isPending}>
                  Angre
                </Button>
              )}
              <Button size="sm" onClick={save} disabled={!dirty || update.isPending}>
                {update.isPending ? "Lagrer ..." : "Lagre stilguide"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
